/**
 * The operator's half of the SOS call's signalling.
 *
 * One request/response exchange per peer connection: POST an SDP offer, get an SDP answer. There is
 * no trickle ICE and no polling loop — the broker hands the offer to the media server and returns
 * its answer in the same response, so the whole "signalling channel" is two fetches.
 *
 * Addressed by ROBOT, never by stream. The console knows which helmet it is calling but not which
 * session or which transcoded stream name that maps to, and shipping those to the browser only to
 * have them sent back would be a round trip whose only effect is to let the two disagree.
 */

/** The stream is authorised but nothing is publishing on it YET — the normal first seconds. */
export class CallNotReadyError extends Error {
  constructor() {
    super('the call stream has not started yet')
    this.name = 'CallNotReadyError'
  }
}

/** The call is over, or was never opened. Distinct from "not ready": retrying will not fix it. */
export class CallGoneError extends Error {
  constructor(readonly status: number) {
    super(`no active call (${status})`)
    this.name = 'CallGoneError'
  }
}

/**
 * Where the operator's two peers exchange SDP.
 *
 * `play` needs no viewer id — every operator on the call plays the same transcoded feed. `push`
 * does, because the operator's microphone publishes under a name of its own and the broker refuses
 * a name that is not this viewer's.
 */
export function callRtcUrl(origin: string, robotId: string, direction: 'play' | 'push', viewerId?: string): string {
  const base = `${origin.replace(/\/+$/, '')}/helmet/rtc/robot/${encodeURIComponent(robotId)}/${direction}`

  // The viewer id rides BOTH directions. It names the operator's own microphone stream on `push`,
  // and on `play` it is how the broker checks this caller is actually on the call — without it the
  // play leg was open to any tenant member, read-only `viewer` accounts included, and that leg
  // carries the WEARER's live microphone.
  return viewerId ? `${base}?viewer=${encodeURIComponent(viewerId)}` : base
}

/** Whether a call is already up on this device — what a second operator needs to know. */
export function callStatusUrl(origin: string, robotId: string): string {
  return `${origin.replace(/\/+$/, '')}/helmet/call/robot/${encodeURIComponent(robotId)}`
}

export interface ExchangeOptions {
  url: string
  offer: string
  token: string | null
  signal?: AbortSignal
  fetchImpl?: typeof fetch
}

/**
 * One SDP exchange.
 *
 * `application/sdp` on BOTH headers is what selects the raw-SDP answer: the broker serves the
 * vendor's JSON envelope by default, because the helmet's own expected shape is unconfirmed, and
 * only a caller that positively looks like WHEP gets SDP back. A browser is that caller.
 *
 * The token rides in `X-Auth-Token` rather than `Authorization` for the same reason the media
 * stream route does — this is the broker's own credential, not the console API's.
 */
export async function exchangeCallSdp(opts: ExchangeOptions): Promise<string> {
  const doFetch = opts.fetchImpl ?? fetch
  const res = await doFetch(opts.url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/sdp',
      Accept: 'application/sdp',
      ...(opts.token ? { 'X-Auth-Token': `Bearer ${opts.token}` } : {}),
    },
    body: opts.offer,
    ...(opts.signal ? { signal: opts.signal } : {}),
  })

  // 409 is the broker saying "not yet" — either no call is open on this device, or the stream has
  // not registered. Both are transient by contract and the caller retries; treating either as a
  // hard failure put a red error on a call that was merely still connecting.
  if (res.status === 409) throw new CallNotReadyError()
  if (res.status === 403 || res.status === 404) throw new CallGoneError(res.status)

  if (!res.ok) {
    // The BODY, not just the status. The broker answers a failed exchange with its own reason —
    // 'media server unavailable' when it could not reach the media sidecar, 'empty answer from
    // media server' when it could — and a bare "call signalling failed (502)" threw that away.
    // The distinction is not cosmetic: it separates our media server being down from the reverse
    // proxy in front of it, and an HTML body means the 502 was authored by Caddy and never reached
    // the broker at all. Trimmed and capped because it is rendered to an operator.
    const reason = await res
      .text()
      .then(t => t.trim().slice(0, 120))
      .catch(() => '')

    throw new Error(
      reason ? `call signalling failed (${res.status}): ${reason}` : `call signalling failed (${res.status})`,
    )
  }

  const answer = await res.text()

  if (!answer.trim()) throw new Error('the media server returned an empty answer')

  // NEVER return this trimmed. SDP is a line protocol and every line, including the last, must be
  // terminated — strip the trailing CRLF and Chrome rejects the WHOLE answer with
  //
  //   Failed to parse SessionDescription. a=candidate:… typ host tcptype passive Invalid SDP line.
  //
  // naming whatever line happened to end up last, which sends you looking for a fault in that line.
  // There is none: the identical answer parses when its terminator is intact. Measured against the
  // real media server — raw `…tcptype passive\r\n` succeeds, `.trim()`ed fails, same bytes otherwise.
  //
  // This cost a live call. The scripted probes all passed because they used the raw response body;
  // only pressing Accept on the emergency popup went through here, so the bug was invisible to
  // everything except the real UI path.
  return answer.endsWith('\n') ? answer : `${answer}\r\n`
}

/**
 * A viewer id must be UNGUESSABLE, not merely unique.
 *
 * It names the operator's microphone stream and identifies them as a member of the call, so a
 * predictable one would let someone else publish audio into an emergency they are not part of.
 */
export function newCallViewerId(): string {
  const bytes = new Uint8Array(12)
  crypto.getRandomValues(bytes)

  return `c-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`
}

/**
 * STUN only, deliberately, and this is a real limitation rather than an oversight.
 *
 * The media server advertises the instance's public address directly, so an operator on an ordinary
 * network connects without a relay. An operator behind a symmetric NAT that also blocks the TCP
 * fallback will not — that needs TURN, which is the documented follow-up. Saying so here is better
 * than a silent "connecting…" that never resolves.
 */
export const CALL_ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]
